import type { ArRowId, TypeId } from '#types/System.Artifacts.types';

const CELL_ID_PREFIX = 'artable-cell-';

export function buildCellId(colId: TypeId, rowId: ArRowId): string {
  return `${CELL_ID_PREFIX}${colId}-${rowId}`;
}

export function parseCellId(id: string): { colId: TypeId; rowId: ArRowId } | null {
  if (!id.startsWith(CELL_ID_PREFIX)) return null;
  const rest = id.slice(CELL_ID_PREFIX.length);
  const lastDashIdx = rest.lastIndexOf('-');
  if (lastDashIdx <= 0) return null;
  const colId = rest.slice(0, lastDashIdx) as TypeId;
  const rowId = rest.slice(lastDashIdx + 1) as ArRowId;
  if (!colId || !rowId) return null;
  return { colId, rowId };
}

export function parseCellIdFromElement(el: HTMLElement): { colId: TypeId; rowId: ArRowId } | null {
  let node: HTMLElement | null = el;
  for (let i = 0; i < 8 && node; i++) {
    const id = node.id;
    if (id) {
      const parsed = parseCellId(id);
      if (parsed) return parsed;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * True when the focused element owns an open popup whose own key handling must
 * win, so container navigation has to stand down. `aria-expanded="true"` alone
 * is deliberately not enough: a disclosure control (the RelationshipTree expand
 * caret) sets it while owning no popup, and treating that as an overlay
 * dead-ends form-field navigation on the caret. Every rc-component overlay
 * trigger pairs `aria-expanded` with `aria-haspopup` — the select combobox
 * input, the menu submenu title, the tabs overflow button — so the pair is the
 * test.
 */
export function isOverlayOpen(activeEl: HTMLElement): boolean {
  if (activeEl.closest('.ant-select-open')) return true;
  if (activeEl.getAttribute('aria-expanded') !== 'true') return false;
  return activeEl.hasAttribute('aria-haspopup');
}

export function isTextInputElement(el: HTMLElement): el is HTMLInputElement | HTMLTextAreaElement {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
}

export function caretAtStart(el: HTMLElement): boolean {
  if (!isTextInputElement(el)) return true;
  try {
    const { selectionStart, selectionEnd } = el;
    if (selectionStart == null || selectionEnd == null) return true;
    return selectionStart === 0 && selectionEnd === 0;
  } catch {
    return true;
  }
}

export function caretAtEnd(el: HTMLElement): boolean {
  if (!isTextInputElement(el)) return true;
  try {
    const { selectionStart, selectionEnd, value } = el;
    if (selectionStart == null || selectionEnd == null) return true;
    return selectionStart === value.length && selectionEnd === value.length;
  } catch {
    return true;
  }
}

export function isMultilineEditable(el: HTMLElement): boolean {
  if (el instanceof HTMLTextAreaElement) return true;
  if (el.isContentEditable === true) return true;
  return el.contentEditable === 'true' || el.contentEditable === 'plaintext-only';
}

const isFocusable = (el: HTMLElement): boolean => {
  const tabindex = el.getAttribute('tabindex');
  if (tabindex !== null && tabindex !== '-1') return true;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select' || tag === 'button') return true;
  if (el.isContentEditable) return true;
  return false;
};

const findFocusableDescendant = (root: HTMLElement): HTMLElement | null => {
  return root.querySelector<HTMLElement>(
    [
      'input:not([disabled])',
      'textarea:not([disabled])',
      '[contenteditable="true"]',
      // antd Select search input
      '.ant-select-selection-search-input',
      // any tabbable item
      '[tabindex]:not([tabindex="-1"])',
    ].join(',')
  );
};

export function focusCellById(cellId: string, opts?: { scroll?: boolean }): boolean {
  const el = document.getElementById(cellId) as HTMLElement | null;
  if (!el) return false;

  const target = isFocusable(el) ? el : findFocusableDescendant(el);
  if (!target) return false;

  if (opts?.scroll !== false) {
    try {
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } catch {
      // ignore
    }
  }

  try {
    target.focus();
    return true;
  } catch {
    return false;
  }
}

export function focusCellByIdWithRetry(
  cellId: string,
  retries = 2
): void {
  if (focusCellById(cellId)) return;
  if (retries <= 0) return;

  requestAnimationFrame(() => {
    focusCellByIdWithRetry(cellId, retries - 1);
  });
}

